<div class="h-full flex flex-col">
  <div class="pt-8 pb-5 pl-8 pr-8 flex-shrink-0">
    <!-- header -->
    <div class="pb-5 md:pb-0 flex items justify-between max-h-16">
      <div class="flex items-center gap-4">
        <!-- On mobile, hide back button -->
        <div class="hidden md:block">
          <button mat-icon-button (click)="goBack()" class="!-ml-2">
            <lucide-angular [img]="ArrowLeft" class="w-5 h-5"></lucide-angular>
          </button>
        </div>
        <!-- Title and environment selector -->
        <div class="pt-4 md:pt-0 flex flex-col md:flex-row items-start md:items-center gap-2">
          <h1 class="!mb-0">
            Base de conocimiento
          </h1>
          @if (!isLoadingKnowledgeBase()) {
            <div class="hidden md:flex !mb-0 mx-2 text-light-text-tertiary dark:text-dark-text-tertiary">
              <lucide-angular [img]="ChevronRight" class="w-5 h-5"></lucide-angular>
            </div>
          }
          @if (!isLoadingKnowledgeBase()) {
            <h2 class="!font-normal !mb-0">
              {{ knowledgeBase()?.name || '' }}
            </h2>
          }
          @if (!isLoadingKnowledgeBase()) {
            <div class="hidden md:flex !mb-0 mx-2 text-light-text-tertiary dark:text-dark-text-tertiary">
              <lucide-angular [img]="ChevronRight" class="w-5 h-5"></lucide-angular>
            </div>
          }
          @if (!isLoadingKnowledgeBase()) {
            <button
              [matMenuTriggerFor]="environmentMenu"
              class="flex flex-row items-center gap-1 bg-transparent text-inherit shadow-none border-none p-0 m-0 hover:opacity-80 transition-opacity cursor-pointer focus:outline-none focus:ring-0"
              type="button">
              <h2 class="!font-normal !mb-0">
                {{ environmetFilter() }}
              </h2>
              <lucide-angular [img]="ChevronDown" class="w-4 h-4 text-light-text dark:text-dark-text"></lucide-angular>
            </button>
          }
          <mat-menu #environmentMenu="matMenu" class="themed-menu">
            @for (option of environmetOptions; track option) {
              <button
                mat-menu-item
                (click)="environmetFilter.set(option); onEnvironmentChange()"
                [disabled]="isLoadingKnowledgeBase()">
                <span>{{ option }}</span>
              </button>
            }
          </mat-menu>
        </div>
      </div>
    </div>
    <!-- Generic Data Table -->
    <div class="flex-1 pt-2 min-h-0 overflow-auto dark:[color-scheme:dark]">
      <!-- Identifier custom design -->
      <ng-template #identifierTemplate let-row>
        <div class="flex flex-col items-center gap-1">
          <span class="font-medium text-light-text dark:text-dark-text">{{ row.identifier || '-' }}</span>
          @if (row.versionNumber != null) {
            <span
              class="px-2 py-0.5 text-xs font-semibold rounded-full bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary whitespace-nowrap">
              v{{ row.versionNumber }}
            </span>
          }
        </div>
      </ng-template>
      <!-- Status code custom design -->
      <ng-template #statusTemplate let-row>
        <div class="flex items-start md:items-center justify-start md:justify-center">
          @if (getStatusIconConfig(row); as config) {
            @if (config.showSpinner) {
              <mat-spinner
                diameter="20"
                color="primary"
                [matTooltip]="config.tooltip">
              </mat-spinner>
            }
            @if (!config.showSpinner && config.icon) {
              <lucide-angular
                [img]="config.icon"
                [matTooltip]="config.tooltip"
                [class]="config.cssClass">
              </lucide-angular>
            }
            @if (!config.showSpinner && !config.icon) {
              <span
                class="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {{ row.statusCode }}
              </span>
            }
          }
        </div>
      </ng-template>
      <!-- Actions custom design -->
      <ng-template #actionsTemplate let-row>
        <div class="flex items-start md:items-center justify-start md:justify-center">
          @if (getActionIconConfig(row); as config) {
            @if (config.icon) {
              <lucide-angular
                [img]="config.icon"
                [matTooltip]="config.tooltip"
                [class]="config.cssClass">
              </lucide-angular>
            }
            @if (!config.icon) {
              <span
                class="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {{ config.tooltip }}
              </span>
            }
          }
        </div>
      </ng-template>

      <!-- Question custom design with truncation and modal -->
      <ng-template #questionTemplate let-row>
        <div class="truncate-cell-3-lines cursor-pointer hover:bg-light-bg-tertiary dark:hover:bg-dark-surface-variant rounded px-2 py-1 transition-colors"
          (click)="openCellContentModal('Pregunta', row.question)">
          {{ row.question || '-' }}
        </div>
      </ng-template>

      <!-- Content custom design with truncation and modal -->
      <ng-template #contentTemplate let-row>
        <div class="truncate-cell-3-lines cursor-pointer hover:bg-light-bg-tertiary dark:hover:bg-dark-surface-variant rounded px-2 py-1 transition-colors"
          (click)="openCellContentModal('Contenido', row.content)">
          {{ row.content || '-' }}
        </div>
      </ng-template>

      <!-- Keywords custom design with limited chips and modal -->
      <ng-template #keywordsTemplate let-row>
        <div class="flex flex-wrap gap-2 items-center">
          @if (getKeywordsArray(row.keywords); as keywordsArray) {
            <!-- Show first 2 keywords -->
            @for (keyword of keywordsArray.slice(0, 2); track keyword) {
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary border border-light-primary/20 dark:border-dark-primary/30">
                {{ keyword }}
              </span>
            }
            <!-- Show "+N more" badge if there are more than 2 -->
            @if (keywordsArray.length > 2) {
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-light-bg-tertiary dark:bg-dark-surface-variant text-light-text-secondary dark:text-dark-text-secondary cursor-pointer hover:opacity-80 transition-opacity"
                (click)="openKeywordsModal(row.keywords)">
                +{{ keywordsArray.length - 2 }} más
              </span>
            }
            <!-- Show "-" if no keywords -->
            @if (keywordsArray.length === 0) {
              <span class="text-light-text-tertiary dark:text-dark-text-tertiary">-</span>
            }
          }
        </div>
      </ng-template>

      <app-data-table
        [data]="articles()"
        [columns]="tableColumns"
        [actions]="tableActions()"
        [loading]="loading()"
        [config]="tableConfig"
        [serverSidePagination]="true"
        [clientSideSorting]="true"
        [totalServerItems]="totalItems()"
        (selectionChange)="onSelectionChange($event)"
        (pageChange)="onPageChange($event)"
        tableCaption="Lista de artículos">
        <ng-template #customFilters>
          <div class="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-2 pb-2">
            <!-- Synchronization status group -->
            <div class="flex items-center gap-3 p-2">
              <span class="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">Sincronización:</span>
              @if (knowledgeBase()) {
                <div class="flex items-center gap-2">
                  @if (getKnowledgeBaseConfig(knowledgeBase()?.qaSynchronizationStatus ?? null); as config) {
                    <div class="flex items-center gap-1.5"
                      [matTooltip]="'Refrescar página para actualizar estado de sincronización'">
                      @if (config.icon) {
                        <lucide-angular [img]="config.icon" [class]="config.iconClass" class="w-3 h-3 text-light-text-secondary dark:text-dark-text-secondary"></lucide-angular>
                      } @else {
                        <div class="w-2.5 h-2.5 rounded-full" [ngClass]="config.color"></div>
                      }
                      <span class="text-sm text-light-text dark:text-dark-text">QA</span>
                    </div>
                  }
                  <span class="text-light-text-secondary dark:text-dark-text-secondary">/</span>
                  @if (getKnowledgeBaseConfig(knowledgeBase()?.prodSynchronizationStatus ?? null); as config) {
                    <div class="flex items-center gap-1.5"
                      [matTooltip]="'Refrescar página para actualizar estado de sincronización'">
                      @if (config.icon) {
                        <lucide-angular [img]="config.icon" [class]="config.iconClass" class="w-3 h-3 text-light-text-secondary dark:text-dark-text-secondary"></lucide-angular>
                      } @else {
                        <div class="w-2.5 h-2.5 rounded-full" [ngClass]="config.color"></div>
                      }
                      <span class="text-sm text-light-text dark:text-dark-text">PROD</span>
                    </div>
                  }
                </div>
              }
              <span class="text-light-text-secondary dark:text-dark-text-secondary">|</span>
              <button
                (click)="goToSynchronizations()"
                [disabled]="isLoadingKnowledgeBase()"
                [matTooltip]="'Ver historial de sincronizaciones'"
                class="flex items-center gap-1.5 text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text dark:hover:text-dark-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <lucide-angular [img]="History" class="w-4 h-4"></lucide-angular>
                <span>Historial</span>
              </button>
            </div>
            
            <!-- Desktop filters -->
            <div class="hidden md:flex items-center gap-2 justify-end">
              <span class="w-px h-6 bg-light-border dark:bg-dark-border"></span>
              <button
                (click)="openCreateModal()"
                [disabled]="isLoadingKnowledgeBase()"
                class="h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                Nuevo artículo
              </button>
              @if (environmetFilter() !== 'Producción' && environmetFilter() !== 'Eliminados') {
                <button
                  (click)="publishArticles()"
                  [disabled]="isLoadingKnowledgeBase()"
                  class="h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                  Publicar
                </button>
              }
              <button
                [disabled]="isLoadingKnowledgeBase() || environmetFilter() === 'Eliminados'"
                (click)="deleteArticles()"
                class="h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                Borrar
              </button>
              <button
                (click)="openFiltersModal()"
                [disabled]="isLoadingKnowledgeBase() || filtersOpen()"
                class="flex gap-2 items-center h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                <lucide-angular [img]="SlidersHorizontal" class="w-5 h-5 text-light-primary dark:text-dark-primary"></lucide-angular>
                Filtros
              </button>
              <button
                [disabled]="isLoadingKnowledgeBase()"
                [matMenuTriggerFor]="moreActionsMenu"
                class="flex gap-2 items-center h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                <lucide-angular [img]="EllipsisVertical" class="w-5 h-5 text-light-primary dark:text-dark-primary"></lucide-angular>
              </button>
              <mat-menu #moreActionsMenu="matMenu" class="themed-menu">
                @if (isMobile()) {
                  <button mat-menu-item (click)="openCreateModal()" [disabled]="isLoadingKnowledgeBase()">
                    <lucide-angular [img]="Plus" class="w-4 h-4"></lucide-angular>
                    <span>Nuevo artículo</span>
                  </button>
                }
                @if (isMobile() && environmetFilter() !== 'Producción' && environmetFilter() !== 'Eliminados') {
                  <button mat-menu-item (click)="publishArticles()" [disabled]="isLoadingKnowledgeBase()">
                    <lucide-angular [img]="PackageCheck" class="w-4 h-4"></lucide-angular>
                    <span>Publicar</span>
                  </button>
                }
                @if (isMobile()) {
                  <button mat-menu-item (click)="deleteArticles()" [disabled]="isLoadingKnowledgeBase() || environmetFilter() === 'Eliminados'">
                    <lucide-angular [img]="Trash2" class="w-4 h-4"></lucide-angular>
                    <span>Borrar</span>
                  </button>
                }
                <button mat-menu-item (click)="openRetrievalTest()" [disabled]="isLoadingKnowledgeBase() || !knowledgeBase()">
                  <lucide-angular [img]="FlaskConical" class="w-4 h-4"></lucide-angular>
                  <span>Probar KB</span>
                </button>
                <button mat-menu-item (click)="openPicklistModal()" [disabled]="isLoadingKnowledgeBase()">
                  <lucide-angular [img]="ListChecks" class="w-4 h-4"></lucide-angular>
                  <span>Editar selector</span>
                </button>
                <div class="menu-divider"></div>
                <button mat-menu-item (click)="importArticles()" [disabled]="isLoadingKnowledgeBase()">
                  <lucide-angular [img]="Upload" class="w-4 h-4"></lucide-angular>
                  <span>Importar</span>
                </button>
                <button mat-menu-item [matMenuTriggerFor]="exportMenu" [disabled]="isLoadingKnowledgeBase()">
                  <lucide-angular [img]="Download" class="w-4 h-4"></lucide-angular>
                  <span>Exportar</span>
                </button>
              </mat-menu>
              <mat-menu #exportMenu="matMenu" class="themed-menu">
                <button mat-menu-item (click)="exportCurrentViewArticles()" [disabled]="isLoadingKnowledgeBase()">
                  <span>Vista actual</span>
                </button>
                <button mat-menu-item (click)="exportKnowledgeBaseArticles()" [disabled]="isLoadingKnowledgeBase() || !knowledgeBase()">
                  <span>Toda la KB</span>
                </button>
              </mat-menu>
            </div>
            <!-- Mobile filters -->
            <div class="md:hidden flex flex-row gap-2 justify-end w-full pb-2">
              <button
                (click)="openFiltersModal()"
                [disabled]="isLoadingKnowledgeBase() || filtersOpen()"
                class="flex gap-2 items-center h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                <lucide-angular [img]="SlidersHorizontal" class="w-5 h-5 text-light-primary dark:text-dark-primary"></lucide-angular>
                Filtros
              </button>
              <button
                [disabled]="isLoadingKnowledgeBase()"
                [matMenuTriggerFor]="moreActionsMenu"
                class="flex gap-2 items-center h-10 px-6 bg-light-primary/10 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                <lucide-angular [img]="EllipsisVertical" class="w-5 h-5 text-light-primary dark:text-dark-primary"></lucide-angular>
              </button>
            </div>
          </div>

        </ng-template>
      </app-data-table>
    </div>
  </div>
</div>

